import _ from 'lodash';
import { LOAD_STATUS } from '@/workbook/workbook.store';
import { HomeScreenUtilitiesService } from '@/hybrid/homescreen/homeScreen.utilities.service';
import {
  archiveWorksheet as archiveWorksheetUtil,
  branchWorksheet as branchWorksheetUtil,
  createWorksheet,
  getWorksheet,
  moveWorksheet as moveWorksheetUtil,
  restoreWorksheet as restoreWorksheetUtils,
  setName as setNameUtil,
} from '@/hybrid/worksheets/worksheets.utilities';
import { APP_STATE, CREATED_BY_SEEQ_WORKBENCH, LIMIT_SIZE_1000, MAX_NAME_LENGTH } from '@/main/app.constants';
import { sqItemsApi, sqTreesApi, sqWorkbooksApi } from '@/sdk';
import { SeeqNames } from '@/main/app.constants.seeqnames';
import { getNextName, isViewOnlyWorkbookMode } from '@/hybrid/utilities/utilities';
import { errorToast, warnToast } from '@/hybrid/utilities/toast.utilities';
import { getItemACL } from '@/hybrid/utilities/acl.utilities';
import { flux } from '@/core/flux.module';
import { PUSH_IGNORE } from '@/core/flux.service';
import { addAssetsProperty } from '@/hybrid/utilities/httpHelpers.utilities';
import { TREND_PANELS } from '@/trendData/trendData.constants';
import { WORKBOOK_DISPLAY } from '@/workbook/workbook.constants';
import { sqWorkbookStore } from '@/core/core.stores';
import { clearWorkstepAction } from '@/worksteps/worksteps.actions';
import { emitWorkbook, emitWorkbooks, onWorkbook } from '@/services/notifier.service';

/**
 * Flux workbook actions
 */

/**
 * Returns the name, if specified, or computes one, which is the next available number, to use for the worksheet.
 *
 * @param name - Optional name for the worksheet. If no name is provided, a name is
 *   automatically computed.
 * @param prefix - Optional prefix for the auto-generated worksheet name. If no prefix is
 *   provided, the name is the next number.
 * @return Worksheet name to use
 */
export function computeWorksheetName({ name = '', prefix = '' } = {}): string {
  if (name) {
    return name;
  }

  return getNextName(_.map(sqWorkbookStore.worksheets, 'name'), prefix, true);
}

/**
 * Makes an asynchronous call to load the pinned items.
 * Before load the loading status will be set to LOADING and when loading is finished it either set to LOADED or
 * to ERROR
 *
 * @param workbookId - The ID of the workbook for which the pinned items are requested to be loaded.
 */
export function fetchPinnedItems(workbookId: string) {
  flux.dispatch('WORKBOOK_SET_PINNED_LOAD_STATUS', {
    loadStatus: LOAD_STATUS.LOADING,
  });
  return sqItemsApi
    .getPinnedItems({ sourceId: workbookId })
    .then(({ data }) => addAssetsProperty(data))
    .then(_.property('items'))
    .then((items) => {
      flux.dispatch('WORKBOOK_SET_PINNED', { items });
      flux.dispatch('WORKBOOK_SET_PINNED_LOAD_STATUS', {
        loadStatus: LOAD_STATUS.LOADED,
      });
    })
    .catch(() => {
      flux.dispatch('WORKBOOK_SET_PINNED_LOAD_STATUS', {
        loadStatus: LOAD_STATUS.ERROR,
      });
    });
}

/**
 * Makes an asynchronous call to load the recently accessed items.
 * Before load the loading status will be set to LOADING and when loading is finished it either set to LOADED or
 * to ERROR
 *
 * @param workbookId - The ID of the workbook for which the recently accessed items are requested to be
 * loaded.
 */
export function fetchRecentlyAccessedItems(workbookId: string) {
  flux.dispatch('WORKBOOK_SET_RECENTLY_ACCESSED_LOAD_STATUS', {
    loadStatus: LOAD_STATUS.LOADING,
  });
  return sqWorkbooksApi
    .getRecentlyAccessed({ workbookId })
    .then(({ data }) => addAssetsProperty(data))
    .then(_.property('items'))
    .then((items) => {
      flux.dispatch('WORKBOOK_SET_RECENTLY_ACCESSED', { items });
      flux.dispatch('WORKBOOK_SET_RECENTLY_ACCESSED_LOAD_STATUS', {
        loadStatus: LOAD_STATUS.LOADED,
      });
    })
    .catch(() => {
      flux.dispatch('WORKBOOK_SET_RECENTLY_ACCESSED_LOAD_STATUS', {
        loadStatus: LOAD_STATUS.ERROR,
      });
    });
}

export function fetchAssetGroups(workbookIds: string[]) {
  flux.dispatch('WORKBOOK_SET_ASSET_GROUPS_LOAD_STATUS', {
    loadStatus: LOAD_STATUS.LOADING,
  });
  return sqTreesApi
    .getTreeRootNodes({
      scope: _.compact(workbookIds),
      properties: [`${SeeqNames.Properties.TreeType}=${CREATED_BY_SEEQ_WORKBENCH}`],
      limit: LIMIT_SIZE_1000,
    })
    .then(({ data }) => {
      flux.dispatch('WORKBOOK_SET_ASSET_GROUPS', { items: data.children });
      flux.dispatch('WORKBOOK_SET_ASSET_GROUPS_LOAD_STATUS', {
        loadStatus: LOAD_STATUS.LOADED,
      });
    })
    .catch(() => {
      flux.dispatch('WORKBOOK_SET_ASSET_GROUPS_LOAD_STATUS', {
        loadStatus: LOAD_STATUS.ERROR,
      });
    });
}

/**
 * Makes asynchronous calls as required and loads the workbook.
 *
 * @param workbookId - The ID of the workbook to be loaded.
 * @param workbookDisplay - The view-mode for the workbook. One of WORKBOOK_DISPLAY
 * @returns {Promise} that resolves with the workbook data when the workbook is loaded.
 */
export function loadWorkbook(
  sqHomeScreenUtilities: HomeScreenUtilitiesService,
  workbookId: string,
  workbookDisplay = WORKBOOK_DISPLAY.VIEW,
) {
  if (workbookDisplay !== WORKBOOK_DISPLAY.PRESENT) {
    fetchPinnedItems(workbookId);
    fetchRecentlyAccessedItems(workbookId);
    fetchAssetGroups([workbookId]);
  }

  return sqHomeScreenUtilities.getWorkbook(workbookId).then((workbook) => {
    workbook = _.assign(workbook, {
      isArchived: _.get(workbook, 'isArchived', false),
      workbookDisplay,
    });
    // Waiting to set the workbookDisplay until the workbook is fetched and then using PUSH_IGNORE is important here
    // to avoid state leak from one worksheet to another. This is the first step of loading the state for a
    // worksheet and the stateSynchronizer uses workbookDisplay to prevent erroneous workstep pushes.
    flux.dispatch('WORKBOOK_SET', workbook, PUSH_IGNORE);
    return workbook;
  });
}

/**
 * Sets a workbook's or folder's name, removing any HTML markup, if name is less than maximum
 *
 * @param workbookId - The ID of the workbook or folder
 * @param name - The new name of the workbook or folder
 * @param originalName - The original name of the workbook or folder
 * @returns {Promise} A promise that resolves with the new name when the workbook or folder is updated.
 */
export function setWorkbookName(
  workbookId: string,
  name: string,
  originalName: string,
  sqHomeScreenUtilities: HomeScreenUtilitiesService,
) {
  if (name.length > MAX_NAME_LENGTH.WORKBOOK) {
    flux.dispatch('WORKBOOK_SET_PROP', { name: originalName });
    flux.dispatch('WORKBENCH_SET_NAME', { name: originalName, workbookId });
    warnToast({
      messageKey: 'NAME_LENGTH_ERROR',
      messageParams: { count: MAX_NAME_LENGTH.WORKBOOK },
    });
    return Promise.resolve();
  } else {
    // Rename locally immediately. If the server call fails, we'll revert the change.
    flux.dispatch('WORKBOOK_SET_PROP', { name });
    flux.dispatch('WORKBENCH_SET_NAME', { name, workbookId });

    return sqHomeScreenUtilities
      .setProperty(SeeqNames.Properties.Name, workbookId, name)
      .then(() => {
        emitWorkbooks();
        emitWorkbook(workbookId);
      })
      .then(() => name)
      .catch(() => {
        warnToast({ messageKey: 'INVALID_NAME' });

        flux.dispatch('WORKBOOK_SET_PROP', { name: originalName });
        flux.dispatch('WORKBENCH_SET_NAME', {
          name: originalName,
          workbookId,
        });
      });
  }
}

/**
 * Sets the workbook's description
 *
 * @param workbookId - The ID of the workbook
 * @param description - The description of the workbook
 * @returns {Promise} A promise that resolves when the workbook is updated.
 */
export function setWorkbookDescription(
  workbookId: string,
  description: string,
  sqHomeScreenUtilities: HomeScreenUtilitiesService,
) {
  return sqHomeScreenUtilities
    .setProperty('Description', workbookId, description)
    .then(() => {
      flux.dispatch('WORKBOOK_SET_PROP', { description });
    })
    .catch(() => warnToast({ messageKey: 'INVALID_NAME' }));
}

/**
 * Sets the workbook's display mode
 *
 * @param workbookDisplay - The display mode of the workbook
 */
export function setWorkbookDisplayMode(workbookDisplay: string) {
  flux.dispatch('WORKBOOK_SET_PROP', { workbookDisplay });
}

/**
 * Creates a new worksheet in a workbook
 *
 * @param workbookId - ID of workbook in which to create a new worksheet
 * @param options - Additional options.
 * @param {String} [options.name] - Optional name for the worksheet. If no name is provided, a name is
 *   automatically generated.
 * @param {String} [options.prefix] - Optional prefix for the auto-generated worksheet name. If no prefix is
 *   provided, the name is the next number.
 * @returns {Promise} A promise that resolves with the worksheet when the worksheet has been added
 */
export function addWorksheetToWorkbook(workbookId: String, options?: object) {
  clearWorkstepAction();
  return createWorksheet(workbookId, computeWorksheetName(options)).then((worksheet) => {
    // Add worksheet to store
    flux.dispatch('WORKBOOK_ADD_WORKSHEET', { worksheet });

    emitWorkbook(workbookId);
    return worksheet;
  });
}

/**
 * Creates a branch of a worksheet and adds it as the last worksheet in the workbook
 *
 * @param workbookId - ID of the workbook on which the worksheet exists
 * @param worksheetId - ID of the worksheet to branch
 * @param options - Additional options.
 * @param {String} [options.name] - Optional name for the worksheet. If no name is provided, a name is
 *   automatically generated.
 * @param {String} [options.prefix] - Optional prefix for the auto-generated worksheet name. If no prefix is
 *   provided, the name is the next number.
 */
export function branchWorksheet(workbookId: string, worksheetId: string, options: object, $state: ng.ui.IStateService) {
  return branchWorksheetUtil(workbookId, worksheetId, computeWorksheetName(options)).then((worksheet) => {
    // Add worksheet to store
    flux.dispatch('WORKBOOK_ADD_WORKSHEET', { worksheet });

    emitWorkbook(workbookId);
    $state.go(APP_STATE.WORKSHEET, {
      workbookId,
      worksheetId: worksheet.worksheetId,
    });
  });
}

/**
 * Restores a worksheet
 *
 * @param workbookId - ID of the workbook on which the worksheet exists
 * @param worksheetId - ID of the worksheet to branch
 * @param worksheetIndex - position where the worksheet will be restored
 * @returns {Promise} a promise that resolves with the restored worksheet
 */
export function restoreWorksheet(
  workbookId: string,
  worksheetId: string,
  worksheetIndex: number,
  $state: ng.ui.IStateService,
) {
  return restoreWorksheetUtils(worksheetId)
    .then(() => getWorksheet($state.params.workbookId, worksheetId))
    .then((worksheet) => {
      // Re-add worksheet to store
      flux.dispatch('WORKBOOK_ADD_WORKSHEET', {
        worksheet,
        position: worksheetIndex,
      });

      emitWorkbook(workbookId);
      return worksheet;
    });
}

/**
 * Sets the name on a worksheet, removing any HTML markup, if name is less than maximum
 *
 * @param worksheetId - GUID for the worksheet to rename
 * @param name - Name to set on the worksheet
 * @return {Promise} that resolves with the new name when the worksheet has been renamed
 */
export function renameWorksheet(worksheetId: string, name: string) {
  const oldName = _.find(sqWorkbookStore.worksheets, ['worksheetId', worksheetId]).name;

  if (name.length > MAX_NAME_LENGTH.WORKSHEET) {
    setWorksheetProperty(worksheetId, 'name', oldName);
    warnToast({
      messageKey: 'NAME_LENGTH_ERROR',
      messageParams: { count: MAX_NAME_LENGTH.WORKSHEET },
    });
    return Promise.resolve(false);
  } else {
    // Rename worksheet locally immediately. If the server call fails, we'll revert the change.
    flux.dispatch('WORKBOOK_RENAME_WORKSHEET', { worksheetId, name });

    return setNameUtil(worksheetId, name)
      .then(() => {
        emitWorkbook(sqWorkbookStore.workbookId);
        return name;
      })
      .catch(() => {
        flux.dispatch('WORKBOOK_RENAME_WORKSHEET', {
          worksheetId,
          name: oldName,
        });
        warnToast({ messageKey: 'INVALID_NAME' });

        return Promise.resolve(false);
      });
  }
}

/**
 * Set a property on one of the worksheets in the current workbook. This adds or changes the property in the
 * sqWorkbookStore.worksheets array.
 *
 * @param worksheetId - ID for the worksheet in the current workbook
 * @param propertyName - Name of property to set on the specified worksheet
 * @param propertyValue - Value of the property to set
 */
export function setWorksheetProperty(worksheetId: string, propertyName: string, propertyValue: string) {
  flux.dispatch('WORKBOOK_SET_WORKSHEET_PROPERTY', {
    worksheetId,
    propertyName,
    propertyValue,
  });
}

/**
 * Moves a worksheet to a new location in the specified workbook. If the call to move the worksheet on the server
 * fails, the move is reverted.
 *
 * @param workbookId - Workbook ID that contains the worksheet
 * @param worksheet - Worksheet to move
 * @param worksheet.worksheetId - ID of the worksheet to be moved
 * @param nextWorksheet - Worksheet marking the destination location
 * @param nextWorksheet.worksheetId - ID of the worksheet marking the destination location
 * @return {Promise} A promise that resolves when the worksheet has been moved
 */
export function moveWorksheetInWorkbook(
  workbookId: string,
  worksheet: { worksheetId: string },
  nextWorksheet?: { worksheetId: string },
) {
  const currentIndex = _.findIndex(sqWorkbookStore.worksheets, ['worksheetId', worksheet.worksheetId]);
  const oldNextId = _.get(sqWorkbookStore.worksheets[currentIndex + 1], 'worksheetId');
  const newNextId = _.get(nextWorksheet, 'worksheetId');

  // Change the store now, assuming the service call succeeds. If it doesn't, we'll revert the change.
  flux.dispatch('WORKBOOK_MOVE_WORKSHEET', {
    worksheetId: worksheet.worksheetId,
    nextWorksheetId: newNextId,
  });

  // Commit the change to the server
  return moveWorksheetUtil(workbookId, worksheet.worksheetId, newNextId).catch((response) => {
    flux.dispatch('WORKBOOK_MOVE_WORKSHEET', {
      worksheetId: worksheet.worksheetId,
      nextWorksheetId: oldNextId,
    });

    errorToast({ httpResponseOrError: response });
  });
}

/**
 * Archives a worksheet.
 *
 * @param workbookId - ID of the workbookId the worksheet belongs to
 * @param worksheetId - ID of the worksheet to be archived
 */
export function archiveWorksheet(workbookId: string, worksheetId: string, $state: ng.ui.IStateService) {
  return archiveWorksheetUtil(worksheetId).then(() => {
    let newSelectedIndex;
    const index = _.findIndex(sqWorkbookStore.worksheets, { worksheetId });

    // Worksheet already removed from store
    if (index < 0) {
      return;
    }

    // If the selected worksheet was archived (removed), then choose the previous worksheet as the new selected
    // worksheet
    if (worksheetId === sqWorkbookStore.worksheets[index].worksheetId) {
      newSelectedIndex = Math.min(Math.max(index - 1, 0), sqWorkbookStore.worksheets.length - 1);
    }

    // Remove worksheet from store
    flux.dispatch('WORKBOOK_REMOVE_WORKSHEET', { worksheetId });

    // If there are worksheets and the selected worksheet was archived (removed), then route to another worksheet
    if (sqWorkbookStore.worksheets.length > 0) {
      if (!_.isUndefined(newSelectedIndex)) {
        $state.go(APP_STATE.WORKSHEET, {
          workbookId,
          worksheetId: sqWorkbookStore.worksheets[newSelectedIndex].worksheetId,
          archived: true,
        });
      }
    } else {
      // If there isn't a worksheet in the workbook, then add one
      addWorksheetToWorkbook(workbookId).then(function (worksheet) {
        $state.go(APP_STATE.WORKSHEET, {
          workbookId,
          worksheetId: worksheet.worksheetId,
          archived: true,
        });
      });
    }

    emitWorkbook(workbookId);
  });
}

/**
 * Adds an item to the pinned list for the specified workbook and then updates the pinned list
 * with the latest.
 *
 * @param workbookId - The ID of the workbook
 * @param id - The ID of the item to be pinned
 * @return {Promise} Resolves when pin is complete
 */
export function pinToWorkbook(workbookId: string, id: string) {
  return sqItemsApi.pinItem({ sourceId: workbookId, itemId: id }).then(({ data }) => {
    addAssetsProperty(data);
    flux.dispatch('WORKBOOK_SET_PROP', { pinned: data.items });
  });
}

/**
 * Removes an item from the pinned list for the specified workbook and then updates the pinned list
 * with the latest.
 *
 * @param workbookId - The ID of the workbook
 * @param id - The ID of the item to be unpinned
 * @return {Promise} Resolves when unpin is complete
 */
export function unpinToWorkbook(workbookId: string, id: string) {
  return sqItemsApi.unpinItem({ sourceId: workbookId, itemId: id }).then(({ data }) => {
    addAssetsProperty(data);
    flux.dispatch('WORKBOOK_SET_PROP', { pinned: data.items });
  });
}

/**
 * Adds an item to the recently accessed list for the specified workbook and then updates the recently accessed list
 * with the latest.
 *
 * @param workbookId - The ID of the workbook
 * @param id - The ID of the item that was recently accessed
 */
export function addRecentlyAccessed(workbookId: string, id: string, sqHomeScreenUtilities: HomeScreenUtilitiesService) {
  if (!isViewOnlyWorkbookMode()) {
    sqHomeScreenUtilities
      .addRecentlyAccessed(workbookId, id)
      .then((data: any) => flux.dispatch('WORKBOOK_SET_PROP', { recentlyAccessed: data.items }))
      // Ignore backend errors (for example when adding an annotation with only read permissions to the
      // workbook/signal)
      .catch(_.noop);
  }
}

/**
 * Executes the set of actions to change the view for a worksheet in viewing mode. The comment tab is selected and
 * the left panel is then hidden. The bottom panels are also reset to the default height.
 */
export function resetViewingState($injector: ng.auto.IInjectorService) {
  $injector.invoke(function (sqWorksheetActions, sqTrendActions, sqInvestigateActions) {
    // Tools like Manual Condition can still effect the look of the trend in presentation mode, close them
    sqInvestigateActions.close();
    // Switch to comments tab
    sqWorksheetActions.tabsetChangeTab('sidebar', 'annotate');
    // Un-collapse the browse column
    sqWorksheetActions.setBrowsePanelCollapsed(false);
    // Reset the panels to default height
    sqTrendActions.setPanelProps(TREND_PANELS.BOTTOM, { height: '30%' });
  });
}

/**
 * A helper function that sets up websocket workbook notification
 */
export function setupNotifierForWorkbook(
  $state: ng.ui.IStateService,
  sqHomeScreenUtilities: HomeScreenUtilitiesService,
) {
  onWorkbook(function (workbookId: string) {
    if (workbookId === $state.params.workbookId) {
      loadWorkbook(sqHomeScreenUtilities, workbookId, sqWorkbookStore.workbookDisplay);
    }
  });
}

/**
 * Fetches the ACL for the current workbook and persist that information to the store.
 *
 * @returns {Promise} that resolves once the ACL was retrieved and saved to the store.
 */
export function setWorkbookACL($state: ng.ui.IStateService) {
  return getItemACL($state.params.workbookId)
    .then((acl) => {
      flux.dispatch('WORKBOOK_SET_ACL', { acl });
    })
    .catch(() => {
      flux.dispatch('WORKBOOK_SET_ACL', { acl: undefined });
    });
}

/**
 * Changes the item owner to the one specified.
 *
 * @param itemId - The ID of the item (workbook or folder) to change owner of
 * @param user - The new owner
 * @param user.id - The ID of the new owner
 * @param user.name - The name of the new owner
 * @returns {Promise} A promise that resolves when the workbook owner has been updated.
 */
export function setItemOwner(itemId: string, user: { id: string; name: string }) {
  return sqItemsApi.changeOwner({ itemId, newOwnerId: user.id }).then(() => flux.dispatch('WORKBOOK_SET_OWNER', user));
}

/**
 * Changes the topic renderer to the one specified.
 *
 * @param itemId - The ID of the topic to change renderer of
 * @param user - The new renderer
 * @param user.id - The ID of the new renderer
 * @param user.name - The name of the new renderer
 * @returns {Promise} A promise that resolves when the workbook renderer has been updated.
 */
export function setItemRenderer(itemId: string, user: { id: string; name: string }) {
  return sqWorkbooksApi.setRenderer({ id: itemId, identityId: user.id }).then(() => {
    flux.dispatch('REPORT_SET_RENDERER', user);
  });
}

/**
 * Changes the viewers list to the one specified in the payload.
 *
 * @param payload The websocket message containing a list of viewers
 * @param payload.subscribers A list of subscribers to this workbook.
 */
export function setViewers(payload: { subscriberParameters: object }) {
  flux.dispatch('WORKBOOK_SET_VIEWERS', payload.subscriberParameters);
}
